import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { API_BASE_URL } from '../config';

interface Quote {
  id: string;
  quoteNumber: string;
  customerName: string;
  customerEmail: string;
  totalAmount: number;
  createdAt: string;
}

interface NotificationSettings {
  enabled: boolean;
  soundEnabled: boolean;
}

const SETTINGS_KEY = 'mt-backoffice:notification-settings';

// Web Audio API for notification sound
class NotificationSound {
  private audioContext: AudioContext | null = null;

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  play() {
    try {
      const context = this.getAudioContext();
      const now = context.currentTime;

      // Create two quick tones for a clean "ding-ding" notification
      // First tone (higher pitch)
      const osc1 = context.createOscillator();
      const gain1 = context.createGain();

      osc1.connect(gain1);
      gain1.connect(context.destination);

      osc1.frequency.value = 1760; // A6 - high and clear
      osc1.type = 'sine';

      // Quick attack and decay
      gain1.gain.setValueAtTime(0, now);
      gain1.gain.linearRampToValueAtTime(0.15, now + 0.005);
      gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.06);

      osc1.start(now);
      osc1.stop(now + 0.06);

      // Second tone (even higher, quick follow-up)
      const osc2 = context.createOscillator();
      const gain2 = context.createGain();

      osc2.connect(gain2);
      gain2.connect(context.destination);

      osc2.frequency.value = 2217; // C#7 - bright and attention-grabbing
      osc2.type = 'sine';

      // Quick attack and decay, starts slightly after first tone
      gain2.gain.setValueAtTime(0, now + 0.04);
      gain2.gain.linearRampToValueAtTime(0.15, now + 0.045);
      gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.12);

      osc2.start(now + 0.04);
      osc2.stop(now + 0.12);
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  }
}

const notificationSound = new NotificationSound();

export function useNotifications(
  accessToken: string | null,
  userRoles: string[],
  onNewQuote?: (quote: Quote) => void,
  onQuoteViewed?: (quoteId: string) => void,
  canManageQuotes?: boolean,
  onQuoteDeleted?: (quoteId: string) => void,
  canManageMedals?: boolean,
  onNewMedalQuote?: (quote: any) => void,
  onMedalQuoteViewed?: (quoteId: string) => void,
  canManageTrophies?: boolean,
  onNewTrophyQuote?: (quote: any) => void,
  onTrophyQuoteViewed?: (quoteId: string) => void,
  canManageTombstones?: boolean,
  onNewTombstoneQuote?: (quote: any) => void,
  onTombstoneQuoteViewed?: (quoteId: string) => void,
  onQuoteChanged?: () => void,
  onMedalQuoteChanged?: () => void,
  onTrophyQuoteChanged?: () => void,
  onTombstoneQuoteChanged?: () => void,
  canManagePlacas?: boolean,
  onNewPlacaQuote?: (quote: any) => void,
  onPlacaQuoteViewed?: (quoteId: string) => void,
  onPlacaQuoteChanged?: () => void
) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [medalUnreadCount, setMedalUnreadCount] = useState(0);
  const [trophyUnreadCount, setTrophyUnreadCount] = useState(0);
  const [tombstoneUnreadCount, setTombstoneUnreadCount] = useState(0);
  const [placaUnreadCount, setPlacaUnreadCount] = useState(0);
  const [settings, setSettings] = useState<NotificationSettings>(() => {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { enabled: true, soundEnabled: true };
      }
    }
    return { enabled: true, soundEnabled: true };
  });

  const eventSourceRef = useRef<EventSource | null>(null);
  // Use refs to store callbacks and settings to avoid re-creating SSE connection when they change
  const onNewQuoteRef = useRef(onNewQuote);
  const onQuoteViewedRef = useRef(onQuoteViewed);
  const onQuoteDeletedRef = useRef(onQuoteDeleted);
  const onNewMedalQuoteRef = useRef(onNewMedalQuote);
  const onMedalQuoteViewedRef = useRef(onMedalQuoteViewed);
  const onNewTrophyQuoteRef = useRef(onNewTrophyQuote);
  const onTrophyQuoteViewedRef = useRef(onTrophyQuoteViewed);
  const onNewTombstoneQuoteRef = useRef(onNewTombstoneQuote);
  const onTombstoneQuoteViewedRef = useRef(onTombstoneQuoteViewed);
  const onNewPlacaQuoteRef = useRef(onNewPlacaQuote);
  const onPlacaQuoteViewedRef = useRef(onPlacaQuoteViewed);
  const onQuoteChangedRef = useRef(onQuoteChanged);
  const onMedalQuoteChangedRef = useRef(onMedalQuoteChanged);
  const onTrophyQuoteChangedRef = useRef(onTrophyQuoteChanged);
  const onTombstoneQuoteChangedRef = useRef(onTombstoneQuoteChanged);
  const onPlacaQuoteChangedRef = useRef(onPlacaQuoteChanged);
  const settingsRef = useRef(settings);

  // Keep refs updated
  useEffect(() => {
    onNewQuoteRef.current = onNewQuote;
    onQuoteViewedRef.current = onQuoteViewed;
    onQuoteDeletedRef.current = onQuoteDeleted;
    onNewMedalQuoteRef.current = onNewMedalQuote;
    onMedalQuoteViewedRef.current = onMedalQuoteViewed;
    onNewTrophyQuoteRef.current = onNewTrophyQuote;
    onTrophyQuoteViewedRef.current = onTrophyQuoteViewed;
    onNewTombstoneQuoteRef.current = onNewTombstoneQuote;
    onTombstoneQuoteViewedRef.current = onTombstoneQuoteViewed;
    onNewPlacaQuoteRef.current = onNewPlacaQuote;
    onPlacaQuoteViewedRef.current = onPlacaQuoteViewed;
    onQuoteChangedRef.current = onQuoteChanged;
    onMedalQuoteChangedRef.current = onMedalQuoteChanged;
    onTrophyQuoteChangedRef.current = onTrophyQuoteChanged;
    onTombstoneQuoteChangedRef.current = onTombstoneQuoteChanged;
    onPlacaQuoteChangedRef.current = onPlacaQuoteChanged;
    settingsRef.current = settings;
  }, [onNewQuote, onQuoteViewed, onQuoteDeleted, onNewMedalQuote, onMedalQuoteViewed, onNewTrophyQuote, onTrophyQuoteViewed, onNewTombstoneQuote, onTombstoneQuoteViewed, onNewPlacaQuote, onPlacaQuoteViewed, onQuoteChanged, onMedalQuoteChanged, onTrophyQuoteChanged, onTombstoneQuoteChanged, onPlacaQuoteChanged, settings]);

  // Check if user can manage quotes (ADMIN, EDITOR, or ORCAMENTISTA)
  // Memoize to prevent SSE reconnection on every render when userRoles array reference changes
  const hasQuoteAccess = useMemo(() => {
    return canManageQuotes ?? userRoles.includes('BACKOFFICE_ORCAMENTISTA');
  }, [canManageQuotes, userRoles.join(',')]);

  // Check if user can manage medal quotes
  const hasMedalAccess = useMemo(() => {
    return canManageMedals ?? userRoles.includes('BACKOFFICE_MEDALHAS');
  }, [canManageMedals, userRoles.join(',')]);

  // Check if user can manage trophy quotes
  const hasTrophyAccess = useMemo(() => {
    return canManageTrophies ?? userRoles.includes('BACKOFFICE_TROFEUS');
  }, [canManageTrophies, userRoles.join(',')]);

  // Check if user can manage tombstone quotes
  const hasTombstoneAccess = useMemo(() => {
    return canManageTombstones ?? userRoles.includes('BACKOFFICE_TOMBSTONES');
  }, [canManageTombstones, userRoles.join(',')]);

  // Check if user can manage placa quotes
  const hasPlacaAccess = useMemo(() => {
    return canManagePlacas ?? userRoles.includes('BACKOFFICE_PLACAS');
  }, [canManagePlacas, userRoles.join(',')]);

  // Combined access flag for SSE connection
  const hasAnyNotificationAccess = hasQuoteAccess || hasMedalAccess || hasTrophyAccess || hasTombstoneAccess || hasPlacaAccess;

  // Fetch initial unread count from backend
  const fetchUnreadCount = useCallback(async () => {
    if (!accessToken || !hasQuoteAccess) return;

    try {
      const response = await fetch(`${API_BASE_URL}/backoffice/quotes/unviewed/count`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const payload = await response.json();
        setUnreadCount(payload.data?.count || 0);
      }
    } catch (error) {
      console.error('[Notifications] Failed to fetch unread count:', error);
    }
  }, [accessToken, hasQuoteAccess]);

  // Fetch medal unread count from backend
  const fetchMedalUnreadCount = useCallback(async () => {
    if (!accessToken || !hasMedalAccess) return;

    try {
      const response = await fetch(`${API_BASE_URL}/backoffice/medals/quotes/unviewed/count`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const payload = await response.json();
        setMedalUnreadCount(payload.count ?? payload.data?.count ?? 0);
      }
    } catch (error) {
      console.error('[Notifications] Failed to fetch medal unread count:', error);
    }
  }, [accessToken, hasMedalAccess]);

  // Fetch trophy unread count from backend
  const fetchTrophyUnreadCount = useCallback(async () => {
    if (!accessToken || !hasTrophyAccess) return;

    try {
      const response = await fetch(`${API_BASE_URL}/backoffice/trophies/quotes/unviewed/count`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const payload = await response.json();
        setTrophyUnreadCount(payload.count ?? payload.data?.count ?? 0);
      }
    } catch (error) {
      console.error('[Notifications] Failed to fetch trophy unread count:', error);
    }
  }, [accessToken, hasTrophyAccess]);

  // Fetch tombstone unread count from backend
  const fetchTombstoneUnreadCount = useCallback(async () => {
    if (!accessToken || !hasTombstoneAccess) return;

    try {
      const response = await fetch(`${API_BASE_URL}/backoffice/tombstones/quotes/unviewed/count`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const payload = await response.json();
        setTombstoneUnreadCount(payload.count ?? payload.data?.count ?? 0);
      }
    } catch (error) {
      console.error('[Notifications] Failed to fetch tombstone unread count:', error);
    }
  }, [accessToken, hasTombstoneAccess]);

  // Fetch placa unread count from backend
  const fetchPlacaUnreadCount = useCallback(async () => {
    if (!accessToken || !hasPlacaAccess) return;

    try {
      const response = await fetch(`${API_BASE_URL}/backoffice/placas/quotes/unviewed/count`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const payload = await response.json();
        setPlacaUnreadCount(payload.count ?? payload.data?.count ?? 0);
      }
    } catch (error) {
      console.error('[Notifications] Failed to fetch placa unread count:', error);
    }
  }, [accessToken, hasPlacaAccess]);

  // Fetch unread count on mount and when user logs in
  useEffect(() => {
    if (accessToken && hasQuoteAccess) {
      void fetchUnreadCount();
    }
  }, [accessToken, hasQuoteAccess, fetchUnreadCount]);

  // Fetch medal unread count on mount and when user logs in
  useEffect(() => {
    if (accessToken && hasMedalAccess) {
      void fetchMedalUnreadCount();
    }
  }, [accessToken, hasMedalAccess, fetchMedalUnreadCount]);

  // Fetch trophy unread count on mount and when user logs in
  useEffect(() => {
    if (accessToken && hasTrophyAccess) {
      void fetchTrophyUnreadCount();
    }
  }, [accessToken, hasTrophyAccess, fetchTrophyUnreadCount]);

  // Fetch tombstone unread count on mount and when user logs in
  useEffect(() => {
    if (accessToken && hasTombstoneAccess) {
      void fetchTombstoneUnreadCount();
    }
  }, [accessToken, hasTombstoneAccess, fetchTombstoneUnreadCount]);

  // Fetch placa unread count on mount and when user logs in
  useEffect(() => {
    if (accessToken && hasPlacaAccess) {
      void fetchPlacaUnreadCount();
    }
  }, [accessToken, hasPlacaAccess, fetchPlacaUnreadCount]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  }, [settings]);

  // Request browser notification permission if enabled
  useEffect(() => {
    if (settings.enabled && hasQuoteAccess && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, [settings.enabled, hasQuoteAccess]);

  const showBrowserNotification = useCallback((quote: Quote) => {
    if (!settings.enabled || !hasQuoteAccess) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    const notification = new Notification('Novo Orçamento Recebido', {
      body: `${quote.customerName} - #${quote.quoteNumber}`,
      icon: '/logo-symbol.png',
      badge: '/logo-symbol.png',
      tag: `quote-${quote.id}`,
      requireInteraction: false,
      silent: !settings.soundEnabled
    });

    // Auto-close after 10 seconds
    setTimeout(() => notification.close(), 10000);

    // Handle click - focus window
    notification.onclick = () => {
      window.focus();
      notification.close();
      // The quote will be handled by the App component
    };
  }, [settings.enabled, settings.soundEnabled, hasQuoteAccess]);

  const playSound = useCallback(() => {
    if (settings.soundEnabled && hasQuoteAccess) {
      notificationSound.play();
    }
  }, [settings.soundEnabled, hasQuoteAccess]);

  // Handle new quote event
  const handleNewQuote = useCallback((quote: Quote) => {
    setUnreadCount(prev => prev + 1);
    showBrowserNotification(quote);
    playSound();
    // Trigger callback to refresh quotes list in real-time
    if (onNewQuote) {
      onNewQuote(quote);
    }
  }, [showBrowserNotification, playSound, onNewQuote]);

  // Establish SSE connection - only depends on accessToken and hasAnyNotificationAccess
  // Use refs for callbacks to avoid re-creating connection when callbacks change
  useEffect(() => {
    if (!accessToken || !hasAnyNotificationAccess) {
      return;
    }

    // Avoid duplicate EventSource in dev/StrictMode
    if (eventSourceRef.current && eventSourceRef.current.readyState !== EventSource.CLOSED) {
      return;
    }

    // Connect directly to backend - SSE doesn't work well through Vite proxy (response buffering)
    // CORS is configured on the backend to allow frontend origin
    // Use API_BASE_URL but remove /v1 suffix for SSE endpoint
    const backendUrl = API_BASE_URL.replace(/\/v1$/, '');
    const url = `${backendUrl}/v1/backoffice/sse/notifications`;
    const eventSource = new EventSource(`${url}?token=${accessToken}`);

    eventSourceRef.current = eventSource;

    eventSource.addEventListener('connected', () => {
      // SSE connection established
    });

    eventSource.addEventListener('heartbeat', () => {
      // Heartbeat received - connection alive
    });

    eventSource.addEventListener('new-quote', (event) => {
      try {
        const payload: { type: string; data: Quote } = JSON.parse(event.data);
        setUnreadCount(prev => prev + 1);
        const s = settingsRef.current;
        if (s.enabled && 'Notification' in window && Notification.permission === 'granted') {
          // Same tag as push — browser deduplicates, only one notification shown
          const n = new Notification('Novo Orçamento Recebido', {
            body: `${payload.data.customerName} - #${payload.data.quoteNumber}`,
            icon: '/logo-symbol.png',
            tag: `quote-chapas-${payload.data.quoteNumber}`,
            silent: !s.soundEnabled,
          });
          setTimeout(() => n.close(), 10000);
          n.onclick = () => { window.focus(); n.close(); };
        }
        if (s.soundEnabled) notificationSound.play();
        if (onNewQuoteRef.current) onNewQuoteRef.current(payload.data);
      } catch (error) {
        console.error('[SSE] Failed to parse quote data:', error);
      }
    });

    eventSource.addEventListener('quote-viewed', (event) => {
      try {
        const payload: { type: string; data: { quoteId: string; quoteNumber: string; viewedBy: string; viewedAt: string } } = JSON.parse(event.data);
        // Decrement counter for global notifications
        setUnreadCount(prev => Math.max(0, prev - 1));
        // Trigger callback using ref
        if (onQuoteViewedRef.current) {
          onQuoteViewedRef.current(payload.data.quoteId);
        }
      } catch (error) {
        console.error('[SSE] Failed to parse quote-viewed data:', error);
      }
    });

    eventSource.addEventListener('quote-deleted', (event) => {
      try {
        const payload: { type: string; data: { quoteId: string; quoteNumber: string; deletedAt: string } } = JSON.parse(event.data);
        // Trigger callback using ref to refresh the quotes list
        if (onQuoteDeletedRef.current) {
          onQuoteDeletedRef.current(payload.data.quoteId);
        }
      } catch (error) {
        console.error('[SSE] Failed to parse quote-deleted data:', error);
      }
    });

    eventSource.addEventListener('unviewed-count', (event) => {
      try {
        const payload: { type: string; data: { count: number; timestamp: string } } = JSON.parse(event.data);
        // Update to exact server count for perfect synchronization
        setUnreadCount(payload.data.count);
      } catch (error) {
        console.error('[SSE] Failed to parse unviewed-count data:', error);
      }
    });

    // Medal quote event listeners
    eventSource.addEventListener('new-medal-quote', (event) => {
      try {
        const payload: { type: string; data: any } = JSON.parse(event.data);
        setMedalUnreadCount(prev => prev + 1);
        const s = settingsRef.current;
        if (s.enabled && 'Notification' in window && Notification.permission === 'granted') {
          const n = new Notification('Novo Orçamento de Medalhas', {
            body: `${payload.data.customerName} - #${payload.data.quoteNumber}`,
            icon: '/logo-symbol.png',
            tag: `quote-medalhas-${payload.data.quoteNumber}`,
            silent: !s.soundEnabled,
          });
          setTimeout(() => n.close(), 10000);
          n.onclick = () => { window.focus(); n.close(); };
        }
        if (s.soundEnabled) notificationSound.play();
        if (onNewMedalQuoteRef.current) onNewMedalQuoteRef.current(payload.data);
      } catch (error) {
        console.error('[SSE] Failed to parse medal quote data:', error);
      }
    });

    eventSource.addEventListener('medal-quote-viewed', (event) => {
      try {
        const payload: { type: string; data: { quoteId: string; quoteNumber: string; viewedBy: string; viewedAt: string } } = JSON.parse(event.data);
        // Decrement medal counter for global notifications
        setMedalUnreadCount(prev => Math.max(0, prev - 1));
        // Trigger callback using ref
        if (onMedalQuoteViewedRef.current) {
          onMedalQuoteViewedRef.current(payload.data.quoteId);
        }
      } catch (error) {
        console.error('[SSE] Failed to parse medal-quote-viewed data:', error);
      }
    });

    eventSource.addEventListener('medal-unviewed-count', (event) => {
      try {
        const payload: { type: string; data: { count: number; timestamp: string } } = JSON.parse(event.data);
        // Update to exact server count for perfect synchronization
        setMedalUnreadCount(payload.data.count);
      } catch (error) {
        console.error('[SSE] Failed to parse medal-unviewed-count data:', error);
      }
    });

    // Trophy quote event listeners
    eventSource.addEventListener('new-trophy-quote', (event) => {
      try {
        const payload: { type: string; data: any } = JSON.parse(event.data);
        setTrophyUnreadCount(prev => prev + 1);
        const s = settingsRef.current;
        if (s.enabled && 'Notification' in window && Notification.permission === 'granted') {
          const n = new Notification('Novo Orçamento de Troféus', {
            body: `${payload.data.customerName} - #${payload.data.quoteNumber}`,
            icon: '/logo-symbol.png',
            tag: `quote-trofeus-${payload.data.quoteNumber}`,
            silent: !s.soundEnabled,
          });
          setTimeout(() => n.close(), 10000);
          n.onclick = () => { window.focus(); n.close(); };
        }
        if (s.soundEnabled) notificationSound.play();
        if (onNewTrophyQuoteRef.current) onNewTrophyQuoteRef.current(payload.data);
      } catch (error) {
        console.error('[SSE] Failed to parse trophy quote data:', error);
      }
    });

    eventSource.addEventListener('trophy-quote-viewed', (event) => {
      try {
        const payload: { type: string; data: { quoteId: string; quoteNumber: string; viewedBy: string; viewedAt: string } } = JSON.parse(event.data);
        // Decrement trophy counter for global notifications
        setTrophyUnreadCount(prev => Math.max(0, prev - 1));
        // Trigger callback using ref
        if (onTrophyQuoteViewedRef.current) {
          onTrophyQuoteViewedRef.current(payload.data.quoteId);
        }
      } catch (error) {
        console.error('[SSE] Failed to parse trophy-quote-viewed data:', error);
      }
    });

    eventSource.addEventListener('trophy-unviewed-count', (event) => {
      try {
        const payload: { type: string; data: { count: number; timestamp: string } } = JSON.parse(event.data);
        // Update to exact server count for perfect synchronization
        setTrophyUnreadCount(payload.data.count);
      } catch (error) {
        console.error('[SSE] Failed to parse trophy-unviewed-count data:', error);
      }
    });

    // Tombstone quote event listeners
    eventSource.addEventListener('new-tombstone-quote', (event) => {
      try {
        const payload: { type: string; data: any } = JSON.parse(event.data);
        setTombstoneUnreadCount(prev => prev + 1);
        const s = settingsRef.current;
        if (s.enabled && 'Notification' in window && Notification.permission === 'granted') {
          const n = new Notification('Novo Orçamento de Tombstones', {
            body: `${payload.data.customerName} - #${payload.data.quoteNumber}`,
            icon: '/logo-symbol.png',
            tag: `quote-tombstones-${payload.data.quoteNumber}`,
            silent: !s.soundEnabled,
          });
          setTimeout(() => n.close(), 10000);
          n.onclick = () => { window.focus(); n.close(); };
        }
        if (s.soundEnabled) notificationSound.play();
        if (onNewTombstoneQuoteRef.current) onNewTombstoneQuoteRef.current(payload.data);
      } catch (error) {
        console.error('[SSE] Failed to parse tombstone quote data:', error);
      }
    });

    eventSource.addEventListener('tombstone-quote-viewed', (event) => {
      try {
        const payload: { type: string; data: { quoteId: string; quoteNumber: string; viewedBy: string; viewedAt: string } } = JSON.parse(event.data);
        setTombstoneUnreadCount(prev => Math.max(0, prev - 1));
        if (onTombstoneQuoteViewedRef.current) {
          onTombstoneQuoteViewedRef.current(payload.data.quoteId);
        }
      } catch (error) {
        console.error('[SSE] Failed to parse tombstone-quote-viewed data:', error);
      }
    });

    eventSource.addEventListener('tombstone-unviewed-count', (event) => {
      try {
        const payload: { type: string; data: { count: number; timestamp: string } } = JSON.parse(event.data);
        setTombstoneUnreadCount(payload.data.count);
      } catch (error) {
        console.error('[SSE] Failed to parse tombstone-unviewed-count data:', error);
      }
    });

    // Quote changed event listeners (status or content updated — triggers list refetch + count refresh)
    eventSource.addEventListener('quote-status-changed', () => {
      onQuoteChangedRef.current?.();
      void fetchUnreadCount();
    });

    eventSource.addEventListener('quote-updated', () => {
      onQuoteChangedRef.current?.();
    });

    eventSource.addEventListener('medal-quote-status-changed', () => {
      onMedalQuoteChangedRef.current?.();
      void fetchMedalUnreadCount();
    });

    eventSource.addEventListener('medal-quote-updated', () => {
      onMedalQuoteChangedRef.current?.();
    });

    eventSource.addEventListener('trophy-quote-status-changed', () => {
      onTrophyQuoteChangedRef.current?.();
      void fetchTrophyUnreadCount();
    });

    eventSource.addEventListener('trophy-quote-updated', () => {
      onTrophyQuoteChangedRef.current?.();
    });

    eventSource.addEventListener('tombstone-quote-status-changed', () => {
      onTombstoneQuoteChangedRef.current?.();
      void fetchTombstoneUnreadCount();
    });

    eventSource.addEventListener('tombstone-quote-updated', () => {
      onTombstoneQuoteChangedRef.current?.();
    });

    // Placa quote event listeners
    eventSource.addEventListener('new-placa-quote', (event) => {
      try {
        const payload: { type: string; data: any } = JSON.parse(event.data);
        setPlacaUnreadCount(prev => prev + 1);
        const s = settingsRef.current;
        if (s.enabled && 'Notification' in window && Notification.permission === 'granted') {
          const n = new Notification('Novo Orçamento de Placas', {
            body: `${payload.data.customerName} - #${payload.data.quoteNumber}`,
            icon: '/logo-symbol.png',
            tag: `quote-placas-${payload.data.quoteNumber}`,
            silent: !s.soundEnabled,
          });
          setTimeout(() => n.close(), 10000);
          n.onclick = () => { window.focus(); n.close(); };
        }
        if (s.soundEnabled) notificationSound.play();
        if (onNewPlacaQuoteRef.current) onNewPlacaQuoteRef.current(payload.data);
      } catch (error) {
        console.error('[SSE] Failed to parse placa quote data:', error);
      }
    });

    eventSource.addEventListener('placa-quote-viewed', (event) => {
      try {
        const payload: { type: string; data: { quoteId: string; quoteNumber: string; viewedBy: string; viewedAt: string } } = JSON.parse(event.data);
        setPlacaUnreadCount(prev => Math.max(0, prev - 1));
        if (onPlacaQuoteViewedRef.current) {
          onPlacaQuoteViewedRef.current(payload.data.quoteId);
        }
      } catch (error) {
        console.error('[SSE] Failed to parse placa-quote-viewed data:', error);
      }
    });

    eventSource.addEventListener('placa-unviewed-count', (event) => {
      try {
        const payload: { type: string; data: { count: number; timestamp: string } } = JSON.parse(event.data);
        setPlacaUnreadCount(payload.data.count);
      } catch (error) {
        console.error('[SSE] Failed to parse placa-unviewed-count data:', error);
      }
    });

    eventSource.addEventListener('placa-quote-status-changed', () => {
      onPlacaQuoteChangedRef.current?.();
      void fetchPlacaUnreadCount();
    });

    eventSource.addEventListener('placa-quote-updated', () => {
      onPlacaQuoteChangedRef.current?.();
    });

    eventSource.onopen = () => {
      // Connection opened successfully
    };

    eventSource.onerror = () => {
      // Connection error - EventSource will auto-reconnect
    };

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [accessToken, hasAnyNotificationAccess]); // Only depend on auth state, not on callbacks

  // Update settings
  const updateSettings = useCallback((updates: Partial<NotificationSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  }, []);

  // Decrement unread count
  const decrementUnreadCount = useCallback(() => {
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Reset unread count
  const resetUnreadCount = useCallback(() => {
    setUnreadCount(0);
  }, []);

  return useMemo(() => ({
    unreadCount,
    medalUnreadCount,
    trophyUnreadCount,
    tombstoneUnreadCount,
    placaUnreadCount,
    settings,
    updateSettings,
    decrementUnreadCount,
    resetUnreadCount,
    refreshUnreadCount: fetchUnreadCount,
    refreshMedalUnreadCount: fetchMedalUnreadCount,
    refreshTrophyUnreadCount: fetchTrophyUnreadCount,
    refreshTombstoneUnreadCount: fetchTombstoneUnreadCount,
    refreshPlacaUnreadCount: fetchPlacaUnreadCount,
    hasQuoteAccess,
    hasMedalAccess,
    hasTrophyAccess,
    hasTombstoneAccess,
    hasPlacaAccess
  }), [unreadCount, medalUnreadCount, trophyUnreadCount, tombstoneUnreadCount, placaUnreadCount, settings, updateSettings, decrementUnreadCount, resetUnreadCount, fetchUnreadCount, fetchMedalUnreadCount, fetchTrophyUnreadCount, fetchTombstoneUnreadCount, fetchPlacaUnreadCount, hasQuoteAccess, hasMedalAccess, hasTrophyAccess, hasTombstoneAccess, hasPlacaAccess]);
}
